import { UserProfileRole } from '@@types/api.types';
import { Modal } from '@components/core/modal.component.tsx';
import { Checkbox } from '@components/ui/checkbox.tsx';
import { errorToast } from '@hooks/toast.hook.tsx';
import { useUpdateUserProfile } from '@queries/user-profile.query.ts';
import { useGetCurrentUser } from '@queries/users.query.ts';
import { termsAndConditionsStepSchema } from '@schemas/user-profile.schema.ts';
import React, { useState } from 'react';
import { CompanyTermsContent } from './home-user-terms-and-conditions-modal-company-content.component.tsx';
import { ExpertTermsContent } from './home-user-terms-and-conditions-modal-expert-content.component.tsx';

interface TermsAndConditionsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const TermsAndConditionsModal: React.FC<TermsAndConditionsModalProps> = (props) => {
  const { isOpen, onClose } = props;

  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false);

  const { data: user } = useGetCurrentUser();
  const { mutateAsync: updateUserProfile, isPending: isUpdating } = useUpdateUserProfile();

  const isExpert = user?.profile?.role === UserProfileRole.EXPERT;

  const handleSubmit = async () => {
    const result = termsAndConditionsStepSchema.safeParse({ hasAcceptedTerms });

    if (!result.success) {
      errorToast({ description: 'You must accept the Terms and Conditions to continue.' });
      return;
    }

    try {
      await updateUserProfile({ hasAcceptedTerms: true });
      onClose();
    } catch (error) {
      console.error('Error updating terms acceptance:', error);
    }
  };

  const handleClose = () => {
    if (hasAcceptedTerms || user?.profile?.hasAcceptedTerms) {
      onClose();
    }
  };

  return (
    <Modal
      centered
      footer={{
        rightButton: {
          disabled: !hasAcceptedTerms,
          loading: isUpdating,
          onClick: handleSubmit,
          children: 'Confirm',
        },
      }}
      header={{
        title: 'Terms and Conditions',
      }}
      isOpen={isOpen}
      width="600px"
    >
      <div className="flex flex-col h-full">
        <div className="text-sm text-gray-600 mb-4 shrink-0">
          Please read and accept our Terms and Conditions to continue using the platform.
        </div>

        <div className="border border-gray-200 rounded-lg p-4 overflow-y-auto bg-gray-50 flex-1 mb-4">
          {isExpert ? <ExpertTermsContent /> : <CompanyTermsContent />}
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <Checkbox
            id="acceptTerms"
            checked={hasAcceptedTerms}
            onCheckedChange={(checked) => {
              setHasAcceptedTerms(checked === true);
            }}
          />
          <label
            htmlFor="acceptTerms"
            className="text-sm font-medium text-gray-700"
          >
            I Agree to the Terms and Conditions
          </label>
        </div>
      </div>
    </Modal>
  );
};
